import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Typography, Tag, Space,
  Button, Input, Form, Row, Col,
  Tooltip, Segmented,
  Avatar, message,
  Badge
} from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { userActions } from '../../actions/userActions';
import { userService } from '../../services/userService';
import Paginations from '../../components/pagination';
import ConfirmModal from '../../components/confirmModal';
import UsersFormModal from '../../components/modals/usersFormModal';
import TableData from '../../components/table';
import { commonActions } from '../../actions/commonActions';
import FilterPopover from '../../components/filter';
import { categoryActions } from '../../actions/categoryActions';
import { getblobLink } from '../../services/helper';
import GuardedButton from '../../components/guardedButton';
import { useCurrentPageAccess }
  from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';

const { Text } = Typography;

type UserRole = 'STUDENT' | 'INSTRUCTOR' | 'STAFF';

interface UserItem {
  key: string;
  role: UserRole;
  email?: string;
  phoneNumber?: string;
  status?: string;
  address?: string;
  createdAt?: string;
  firstName?: string;
  createdBy?: string;
  lastName?: string;
  dateOfBirth?: string;
  enrolledNumber?: string;
  service?: string;
  gender?: string;
}

const UserList: React.FC = () => {

  const hasFetchedInitialData = useRef(false);
  const mountCount = useRef(0);
  const pageAccess = useCurrentPageAccess();
  const [roleFilter, setRoleFilter] = useState<'ALL' | UserRole | 'STAFF'>('ALL');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [mutableUsers, setMutableUsers] = useState<any[] | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isFilterData, setIsFilter] = useState(false);
  const [filterData, setIsFilterData] = useState({ role: "", gender: "", status: "" });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<any>({});

  const defaultPayLoad = { page: 1, limit: 10 };
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.limit));
  const [filterForm] = Form.useForm();
  const hasFetchedCounts = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [roleCounts, setRoleCounts] = useState<Record<string, { current: number; total: number }>>({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const prevListingRef = useRef(false);

  const roleData = useSelector((state: any) => state.roleDataReducer.roleData);
  const userCreateState = useSelector((state: any) => state.userCreateReducer);
  const isCreating = Boolean(userCreateState?.isLoading);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const userListState = useSelector((state: any) => state.userListReducer);
  const isListing = Boolean(userListState?.isLoading);
  const [user, setUser] = useState<any>({});
  const uploadState = useSelector((state: any) => state.fileUploadUserReducer);
  const sendInvitationState = useSelector((state: any) => state.sendInvitationReducer);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [invitingAction, setInvitingAction] = useState<'invite' | 'revoke' | null>(null);


  const totalRecords = userListState?.userList?.totalCount;
  const displayCount = userListState?.totalCount ?? userListState?.userList?.totalCount ?? (Array.isArray(mutableUsers) ? mutableUsers.length : 0);

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    dispatch(commonActions.getService({ isActive: true }));
    dispatch(categoryActions.getCategory({ isActive: true }));
    dispatch(userActions.userRoleRequest());
  }, [dispatch]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab')?.toString().toUpperCase();
      if (tab && ['ALL', 'STUDENT', 'INSTRUCTOR', 'STAFF'].includes(tab)) {
        setRoleFilter(tab as any);
        setCurrentPage(1);
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  useEffect(() => {
    mountCount.current += 1;
    if (mountCount.current === 2 && currentPage === 1 && !debouncedSearchText?.trim()) {
      return;
    }
    if (roleFilter !== 'ALL' && (!Array.isArray(roleData) || roleData.length === 0)) {
      return;
    }
    const payload: any = {
      pageNumber: currentPage,
      pageSize,
    };
    if (debouncedSearchText) {
      payload.searchText = debouncedSearchText;
    }
    if (isFilterData && filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    if (roleFilter && roleFilter !== 'ALL' && Array.isArray(roleData)) {
      const matchedRole = roleData.find((r: any) => {
        const roleKey = (r.role ?? r.roleName ?? '').toString().toUpperCase();
        return roleKey === roleFilter;
      });
      if (matchedRole) payload.role = matchedRole._id;
    }
    dispatch(userActions.userListRequest(payload));
  }, [currentPage, pageSize, debouncedSearchText, isFilterData, filterData, roleFilter, roleData, dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText) {
      setCurrentPage(1);
    }
  }, [debouncedSearchText]);

  useEffect(() => {
    const listCandidate = Array.isArray(userListState?.userList?.users)
      ? userListState.userList.users
      : Array.isArray(userListState?.userList)
        ? userListState.userList
        : null;
    if (Array.isArray(listCandidate)) {
      setMutableUsers(listCandidate);
    }
  }, [userListState?.userList]);


  useEffect(() => {
    const usersArr = Array.isArray(userListState?.userList?.users) ? userListState.userList.users : [];
    const total = userListState?.userList?.totalCount ?? displayCount;
    const current = usersArr.length;
    setRoleCounts((prev) => ({ ...prev, [roleFilter]: { current, total } }));
  }, [userListState?.userList]);

  useEffect(() => {
    if (!Array.isArray(roleData) || roleData.length === 0) return;

    const getRoleId = (roleKey: string) => {
      const matched = roleData.find((r: any) => ((r.role ?? r.roleName) || '').toString().toUpperCase() === roleKey);
      return matched?._id;
    };

    const rolesToFetch = [
      { key: 'ALL', roleId: undefined },
      { key: 'STUDENT', roleId: getRoleId('STUDENT') },
      { key: 'INSTRUCTOR', roleId: getRoleId('INSTRUCTOR') },
      { key: 'STAFF', roleId: getRoleId('STAFF') },
    ];

    const fetchCounts = async () => {
      const counts: Record<string, { current: number; total: number }> = {};
      await Promise.all(rolesToFetch.map(async (r) => {
        try {
          const params: any = { pageNumber: 1, pageSize };
          // include current search text
          if (debouncedSearchText) params.searchText = debouncedSearchText;
          // include active filters except the role filter (we override role per tab)
          if (isFilterData && filterData && typeof filterData === 'object') {
            Object.entries(filterData).forEach(([k, v]) => {
              if (k === 'role') return; // skip role here
              if (v !== undefined && v !== null && String(v).trim() !== '') {
                params[k] = v;
              }
            });
          }
          if (r.roleId) params.role = r.roleId;
          const resp: any = await userService.listUsers(params);
          if (resp && resp.success) {
            const ro = resp.resultObject ?? resp;
            const usersArr = Array.isArray(ro.users) ? ro.users : [];
            counts[r.key] = { current: usersArr.length, total: ro.totalCount ?? 0 };
          } else {
            counts[r.key] = { current: 0, total: 0 };
          }
        } catch (e) {
          counts[r.key] = { current: 0, total: 0 };
        }
      }));
      setRoleCounts((prev) => ({ ...prev, ...counts }));
    };

    fetchCounts();
  }, [roleData, isFilterData, filterData, debouncedSearchText, pageSize]);


  const filteredData = useMemo(() => {
    const list: any[] = Array.isArray(mutableUsers) ? mutableUsers : [];
    return list.slice().sort((a: any, b: any) => {
      const aName = (a.name ?? `${a.firstName ?? ''} ${a.lastName ?? ''}`).toString();
      const bName = (b.name ?? `${b.firstName ?? ''} ${b.lastName ?? ''}`).toString();
      return aName.localeCompare(bName);
    });
  }, [mutableUsers]);


  useEffect(() => {
    if (prevListingRef.current && !isListing) {
      setIsTabSwitching(false);
    }
    prevListingRef.current = isListing;
  }, [isListing]);

  const getRoleLabel = (r: any): string => {
    const val = typeof r === 'object' && r !== null ? (r.role ?? r.roleName ?? '') : (r ?? '');
    return val.toString().toUpperCase();
  };

  const getDisplayName = (u: any): string => {
    const name = (u?.name ?? `${u?.firstName ?? ''} ${u?.lastName ?? ''}`).toString().trim();
    return name || '-';
  };

  const handleViewUser = (user: any) => {
    navigate(`/users/view`, { state: { userId: user._id } });
  };

  const refetchUserList = () => {
    const payload: any = {
      pageNumber: currentPage,
      pageSize,
    };
    if (debouncedSearchText) {
      payload.searchText = debouncedSearchText;
    }
    if (isFilterData && filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    if (roleFilter && roleFilter !== 'ALL' && Array.isArray(roleData)) {
      const matchedRole = roleData.find((r: any) => {
        const roleKey = (r.role ?? r.roleName ?? '').toString().toUpperCase();
        return roleKey === roleFilter;
      });
      if (matchedRole) payload.role = matchedRole._id;
    }
    dispatch(userActions.userListRequest(payload));
  };

  const handleInviteUser = (record: any) => {
    setInvitingId(record._id);
    setInvitingAction('invite');
    dispatch(userActions.sendInvitationRequest({ _id: record._id, isInvite: true }));
  };

  const handleRevokeInvite = (record: any) => {
    setInvitingId(record._id);
    setInvitingAction('revoke');
    dispatch(userActions.sendInvitationRequest({ _id: record._id, isRevokeInvite: true }));
  };

  useEffect(() => {
    if (sendInvitationState?.isSuccess && invitingId) {
      message.success(sendInvitationState?.message || 'Action completed successfully');
      setMutableUsers((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((u: any) =>
          u._id === invitingId ? { ...u, isrequestSent: invitingAction === 'invite' } : u
        );
      });
      setInvitingId(null);
      setInvitingAction(null);
    } else if (sendInvitationState?.isFailure && invitingId) {
      message.error(sendInvitationState?.message || 'Action failed');
      setInvitingId(null);
      setInvitingAction(null);
    }
  }, [sendInvitationState?.isSuccess, sendInvitationState?.isFailure]);
  // ...existing code...

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (_: string, record: any) => {
          const genderRaw = (record?.gender || '').toString().toLowerCase();
          return (
            <Space align="center">
              <Avatar size={36} src={genderRaw === 'female' ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`} style={{ backgroundColor: '#f0f2f5', objectFit: 'contain' }} />
              <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                <Text strong>{getDisplayName(record)}</Text>
                {record.email && <Text type="secondary" className="text-small">{record.email}</Text>}
                {record.isManualOnboarding && (
                  <Tag color="gold" style={{ border: '1px solid', marginTop: 4, width: 'fit-content' }}>
                    Manual Onboarding
                  </Tag>
                )}
              </div>
            </Space>
          );
        },
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        render: (role: any) => {
          const label = getRoleLabel(role);
          const color = label === 'STUDENT' ? 'cyan' : label === 'INSTRUCTOR' ? 'purple' :
            label === 'ADMIN' ? 'orange' : label === 'STAFF' ? 'green' : 'default';
          return <Tag color={color} style={{ border: "1px solid" }}>{label || '-'}</Tag>;
        },
        onFilter: (value: any, record: any) => getRoleLabel(record.role) === value,
      },
      {
        title: 'Phone',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        render: (text: string) => <Text>{text || '-'}</Text>,
      },
      {
        title: 'Gender',
        dataIndex: 'gender',
        key: 'gender',
        render: (text: string) => {
          const color = text === 'Male' ? 'blue' : text === 'Female' ? 'pink' : text === 'Transgender' ? 'purple' : 'default';
          return <Tag color={color} style={{ border: "1px solid" }}>{text || '-'}</Tag>;
        },
      },
      {
        title: 'Date of Birth',
        dataIndex: 'dateOfBirth',
        key: 'dateOfBirth',
        render: (text: string, record: UserItem) => (
          <Space size={0}>
            {record.dateOfBirth && <Text type="secondary" className="text-small">{new Date(record.dateOfBirth).toLocaleDateString()}</Text>}
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          const color = status === 'ACTIVE' ? 'green' : status === 'INACTIVE' ? 'default' : status === 'SUSPENDED' ? 'red' : 'default';
          return <Tag color={color} style={{ border: "1px solid" }}>{status || '-'}</Tag>;
        },
      },
      {
        title: 'Created At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (text: string, record: UserItem) => (
          <Space size={0}>
            {record.createdAt ? (
              <Text type="secondary" className="text-small">
                {new Date(record.createdAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
              </Text>
            ) : (
              <Text type="secondary" className="text-small">-</Text>
            )}
          </Space>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: any) => (
          <Space>
            {!pageAccess.isView && <p className="text-small text-gray">No actions available</p>}
            {pageAccess.isView && (
              <Tooltip title="View Details">
                <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewUser(record)} />
              </Tooltip>
            )}
            {record.role && getRoleLabel(record.role) === 'STUDENT' && record.isManualOnboarding && !record.isrequestSent && (
              <Tooltip title="Invite as Real User">
                <Button
                  size="small"
                  type="primary"
                  disabled={invitingId === record._id}
                  onClick={() => handleInviteUser(record)}
                  style={{ width: 90 }}
                >
                  {invitingId === record._id && invitingAction === 'invite' ? <LoadingOutlined /> : 'Invite'}
                </Button>
              </Tooltip>
            )}
            {record.role && getRoleLabel(record.role) === 'STUDENT' && record.isManualOnboarding && record.isrequestSent && (
              <Tooltip title="Revoke Invitation">
                <Button
                  size="small"
                  danger
                  disabled={invitingId === record._id}
                  onClick={() => handleRevokeInvite(record)}
                  style={{ width: 90 }}
                >
                  {invitingId === record._id && invitingAction === 'revoke' ? <LoadingOutlined /> : 'Revoke Invite'}
                </Button>
              </Tooltip>
            )}
          </Space>
        ),
      },
    ];

    if (roleFilter === 'STUDENT' || roleFilter === 'ALL') {
      const enrollmentCol = {
        title: 'Enrollment No',
        key: 'enrolledNo',
        dataIndex: 'enrolledNumber',
        render: (_: string, record: UserItem) => {
          const enrollmentValue = record.enrolledNumber || (record as any).enrollmentNumber;
          return (
            <Space size={0}>
              <Text type="secondary" className="text-small">{enrollmentValue || "-"}</Text>
            </Space>
          );
        },
      };
      baseColumns.splice(6, 0, enrollmentCol);
    }

    return baseColumns;
  }, [roleFilter, pageAccess, getDisplayName, getRoleLabel, handleViewUser, handleInviteUser, handleRevokeInvite, invitingId, invitingAction]);


  useEffect(() => {
    if (userCreateState && userCreateState?.isSuccess && userCreateState?.userData) {
      if (profileImageFile) uploadFile(profileImageFile as File);
      setUser(userCreateState?.userData);
    }
  }, [userCreateState, uploadState]);


  useEffect(() => {
    if (uploadState?.isSuccess && user?._id && profileImageFile) {
      const selectedRole = roleData?.find((r: any) => r.role === user?.role);
      const { userId, otpLoginEnabled, service, isManualOnboarding, category,
        autoActivated, status, message: userMsg, name, ...userDetails } = user;

      // ── Split "Ram kumar" → firstName: "Ram", lastName: "kumar" ──
      const nameParts = (name ?? '').trim().split(/\s+/);
      const firstName = userDetails.firstName || nameParts[0] || '';
      const lastName = userDetails.lastName || nameParts.slice(1).join(' ') || '';

      const payload: any = {
        ...userDetails,
        profileImage: uploadState?.uploadedFile?._id,
        role: selectedRole?._id || null,
        // ← only include firstName/lastName if NOT manual onboarding
        ...(!isManualOnboarding && { firstName, lastName }),
      };

      dispatch(userActions.userUpdateRequest(payload));
      setProfileImageFile(null);
      setUser({});
    }
  }, [uploadState, roleData, user, profileImageFile, dispatch]);



  const uploadFile = (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'userProfile');
      formData.append('ReferenceId', String(userCreateState?.userData?._id));
      dispatch(commonActions.fileUploadUser(formData));
    } catch (err: any) {
      message.error(err?.message || 'Upload failed');
    }
  };

  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
  };

  const handleAddUser = async (values: any, file: any) => {
    try {
      const { ...userData } = values;
      dispatch(userActions.userCreateRequest(userData));
      setProfileImageFile(file);
    } catch (error: any) {
      setIsUploadingImage(false);
    }
  };

  useEffect(() => {
    if (userCreateState?.isSuccess) {
      const createdUser = userCreateState?.userData;
      const studentId = createdUser?._id || createdUser?.userId;
      const categoryId = createdUser?.category?._id || createdUser?.category;
      const serviceId = createdUser?.service?._id || createdUser?.service;

      setIsAddModalVisible(false);

      if (createdUser?.isManualOnboarding && studentId && categoryId) {
        navigate('/enrollments', {
          state: {
            newStudent: studentId,
            category: categoryId,
            service: serviceId || undefined,
          },
        });
        dispatch(userActions.userCreateReset());

      } else {
        const payload: any = {
          pageNumber: currentPage,
          pageSize,
        };
        if (debouncedSearchText) {
          payload.searchText = debouncedSearchText;
        }
        if (isFilterData && filterData && typeof filterData === 'object') {
          Object.entries(filterData).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).trim() !== '') {
              payload[k] = v;
            }
          });
        }
        if (roleFilter && roleFilter !== 'ALL' && Array.isArray(roleData)) {
          const matchedRole = roleData.find((r: any) => {
            const roleKey = (r.role ?? r.roleName ?? '').toString().toUpperCase();
            return roleKey === roleFilter;
          });
          if (matchedRole) payload.role = matchedRole._id;
        }
        dispatch(userActions.userListRequest(payload));
        dispatch(userActions.userCreateReset());
      }
    }
  }, [userCreateState?.isSuccess, navigate, currentPage, pageSize, debouncedSearchText, isFilterData, filterData, roleFilter, roleData, dispatch]);




  const handleApplyFilter = (values: any) => {
    setIsFilter(true);
    setCurrentPage(1);
    const newFilterData: any = { role: "", gender: "", status: "" };
    const appliedFiltersTemp: any = {};
    if (values?.role) {
      newFilterData.role = values.role;
      appliedFiltersTemp.role = values.role;
    }
    if (values?.status) {
      newFilterData.status = values.status;
      appliedFiltersTemp.status = values.status;
    }
    if (values?.gender) {
      newFilterData.gender = values.gender;
      appliedFiltersTemp.gender = values.gender;
    }
    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersTemp);
  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilter(false);
    setIsFilterData({ role: "", gender: "", status: "" });
    setAppliedFilters({});
  };

  const filterFields = useMemo(() => {
    const roleOptions = (Array.isArray(roleData) ? roleData : [])
      .filter((r: any) => {
        const roleKey = (r.role ?? r.roleName ?? '').toString().toLowerCase();
        return ['student', 'instructor', 'staff'].includes(roleKey);
      })
      .map((r: any) => ({
        label: r.roleName ?? r.role,
        value: r._id,
      }));

    return [
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Select Status',
        options: [
          { label: 'Active', value: 'ACTIVE' },
          { label: 'Inactive', value: 'INACTIVE' },
          { label: 'Suspended', value: 'SUSPENDED' },
        ],
      },
      {
        name: 'gender',
        type: 'select',
        label: 'Gender',
        placeholder: 'Select Gender',
        options: [
          { label: 'Male', value: 'Male' },
          { label: 'Female', value: 'Female' },
          { label: 'Transgender', value: 'Transgender' },
        ],
      },
    ];
  }, [roleData]);

  if (isListing && !isTabSwitching) {
    return <SpinnerLoader />;
  }

  const userTabs = [
    { value: 'ALL', label: 'All', count: roleCounts.ALL?.total ?? displayCount },
    { value: 'STUDENT', label: 'Students', count: roleCounts.STUDENT?.total ?? 0 },
    { value: 'INSTRUCTOR', label: 'Instructors', count: roleCounts.INSTRUCTOR?.total ?? 0 },
    { value: 'STAFF', label: 'Staff', count: roleCounts.STAFF?.total ?? 0 },
  ];

  return (
    <div className="users-page">
      <Row justify="space-between" align="middle" className="mb-4">
        <div className='user-toolbar-wrapper'>
          <div className="users-toolbar">
            <Input
              className="users-search-input"
              placeholder="Search by name, email, phone, service or specialization"
              prefix={<SearchOutlined />}
              allowClear
              onChange={e => setSearchText(e.target.value)}
            />
            <FilterPopover
              form={filterForm}
              fields={filterFields}
              initialValues={appliedFilters}
              appliedCount={Object.keys(appliedFilters).length}
              onApply={handleApplyFilter}
              onClear={handleClearFilter}
            />
          </div>
          <div className="users-search-actions">
            <Segmented
              value={roleFilter}
              onChange={(val) => {
                if (val === roleFilter) return;
                setIsTabSwitching(true);
                setRoleFilter(val as 'ALL' | UserRole);
                setCurrentPage(1);
              }}
              options={userTabs.map((tab) => ({
                value: tab.value,
                label: (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                    <span>{tab.label}</span>
                    <Badge
                      count={tab.count}
                      showZero
                      overflowCount={999}
                      style={{
                        backgroundColor: roleFilter === tab.value ? '#1677ff' : '#c7c2c2',
                        color: roleFilter === tab.value ? '#fff' : '#666',
                        fontSize: 11,
                        height: 18,
                        lineHeight: '18px',
                        minWidth: 18,
                        padding: '0 5px',
                        boxShadow: 'none',
                      }}
                    />
                  </div>
                ),
              }))}
            />

          </div>

        </div>
        <Col className='button-col'>
          {pageAccess.isAdd && (
            <GuardedButton type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
              Add User
            </GuardedButton>
          )}
        </Col>
      </Row>
      <div className="driveMynt-card">
        {isTabSwitching ? <SpinnerLoader /> : <div className="users-card-header-">
          {<TableData
            className='user-table'
            rowKey={(record: any) => record._id ?? record.key ?? record.id}
            columns={columns}

            dataSource={filteredData}
            pagination={false}
            rowClassName={(record: any) => (record?.isManualOnboarding ? 'manual-onboarding-row' : '')}
            onRow={(record: any) => ({ onDoubleClick: () => { if (!pageAccess.isView) return; handleViewUser(record); }, style: { cursor: 'pointer' } })}
          />}
        </div>}

        {!isListing && !isTabSwitching && (
          <Paginations
            total={userListState?.userList?.totalCount}
            defaultCurrent={currentPage}
            defaultPageSize={pageSize}
            callback={handleOnPageChange}
          />
        )}
        <ConfirmModal
          visible={confirmVisible}
          title={confirmConfig?.title}
          content={confirmConfig?.content}
          okText={confirmConfig?.okText}
          okType={confirmConfig?.okType}
          onOk={confirmConfig?.onOk ?? (() => setConfirmVisible(false))}
          onCancel={() => setConfirmVisible(false)}
        />
      </div>
      <UsersFormModal
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        loading={isCreating || isUploadingImage}
        roleData={roleData}
        onSubmit={handleAddUser}
        currentUserCounts={{
          students: roleCounts['STUDENT']?.total ?? 0,
          instructors: roleCounts['INSTRUCTOR']?.total ?? 0
        }}
      />
    </div>
  );
};

export default UserList;
